import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  Typography,
  Button,
  Box,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  CircularProgress,
  Chip,
  IconButton,
  Collapse,
  Grid,
  Alert,
  Tooltip,
  useTheme,
  alpha,
  styled,
  Badge
} from '@mui/material';
import { 
  ChevronRight, 
  ChevronDown,
  Plus, 
  User,
  Users, 
  Briefcase, 
  File, 
  LayoutDashboard,
  CheckCircle2,
  Clock,
  Calendar,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Shield
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import workflowService from '../services/api/workflowService';
import config from '../utils/config';
import clientSimilarityService, { SimilarityMatch } from '../services/client/clientSimilarityService';
import { snowflakeService } from '../services/snowflakeService';
import { PaginationControls } from './shared/PaginationControls';
import { SearchInput } from './shared/SearchInput';
import { useWorkflowNavigation } from '../hooks/useWorkflowNavigation';
import ClientAccessControlModal from './ClientAccessControlModal';

interface DatabaseClient {
  client_id: number;
  name: string;
  client_code?: string;
  domains?: string;
  active?: boolean;
  process_status?: 'new' | 'in_progress' | 'completed';
  user_count?: number;
}

interface ClientWorkflowTableProps {
  clients: string[];
  dbClients: DatabaseClient[];
  loadingClients: boolean;
  isUsingDatabaseApproach: boolean;
  workflows: Record<string, any[]>;
  loadingWorkflows: boolean;
  files: Record<string, any[]>;
  loadingFiles: boolean;
  expandedClient: string | null;
  expandedWorkflow: {clientId: string, workflowId: string} | null;
  toggleClientExpansion: (clientId: string | number) => void;
  toggleWorkflowExpansion: (clientId: string | number, workflowId: string | number) => void;
  // Updated to support both string and number IDs, prioritizing number IDs
  goToWorkflow: (clientId: string | number, workflowId: string | number, workflowName?: string) => void;
  handleFileAction: (file: any) => void;
  setDbClientId?: (id: number) => void;
  loadClients?: () => void;
  // Pagination props
  currentPage?: number;
  pageSize?: number;
  totalClients?: number;
  onPageChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
  onPageSizeChange?: (newPageSize: number) => void;
  // Search props
  searchTerm?: string;
  onSearchChange?: (searchTerm: string) => void;
  // Status filter props
  processStatus?: string;
  onProcessStatusChange?: (status: string) => void;
  // Sorting props
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortByChange?: (sortBy: string) => void;
  onSortOrderToggle?: () => void;
}

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: '#ffffff',
  backgroundImage: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
  backdropFilter: 'blur(8px)',
  borderRadius: 16,
  border: '1px solid rgba(226, 232, 240, 0.8)',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    transform: 'translateY(-2px)',
    borderColor: 'rgba(51, 46, 92, 0.3)',
  },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: 8,
  margin: '4px 8px',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: 'rgba(51, 46, 92, 0.08)',
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(51, 46, 92, 0.12)',
    borderLeft: '3px solid #332E5C',
    boxShadow: '0 2px 4px rgba(51, 46, 92, 0.2)',
    '&:hover': {
      backgroundColor: 'rgba(51, 46, 92, 0.16)',
    },
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 8,
  padding: '8px',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: 'rgba(51, 46, 92, 0.1)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
  '&:active': {
    transform: 'translateY(0px)',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 10,
  fontWeight: 500,
  textTransform: 'none',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
  '&:active': {
    transform: 'translateY(0px)',
  },
}));

const GradientTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
})) as typeof Typography;

const ClientWorkflowTable: React.FC<ClientWorkflowTableProps> = ({
  clients,
  dbClients,
  loadingClients,
  isUsingDatabaseApproach,
  workflows,
  loadingWorkflows,
  files,
  loadingFiles,
  toggleClientExpansion,
  toggleWorkflowExpansion,
  goToWorkflow,
  handleFileAction,
  setDbClientId,
  loadClients,
  currentPage = 1,
  pageSize = 20,
  totalClients = 0,
  onPageChange,
  onPageSizeChange,
  searchTerm: propSearchTerm = '',
  onSearchChange,
  processStatus = 'all',
  onProcessStatusChange,
  sortBy = 'name',
  sortOrder = 'asc',
  onSortByChange,
  onSortOrderToggle
}) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isAdmin } = useAuth();
  const theme = useTheme();
  const { openWorkflow: navigateToWorkflow } = useWorkflowNavigation();
  
  const [syncing, setSyncing] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [createWorkflowDialogOpen, setCreateWorkflowDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<string | DatabaseClient | null>(null);
  const workflowNameRef = useRef<HTMLInputElement>(null);
  const [isCreatingWorkflow, setIsCreatingWorkflow] = useState(false);
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const [selectedWorkflowDetails, setSelectedWorkflowDetails] = useState<{
    clientId: string;
    workflowId: string;
    workflow: any;
  } | null>(null);
  const [promptWorkflowAfterClientCreation, setPromptWorkflowAfterClientCreation] = useState(false);
  const [similarityMatches, setSimilarityMatches] = useState<SimilarityMatch[]>([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  // Access control modal state
  const [accessControlModalOpen, setAccessControlModalOpen] = useState(false);
  const [accessControlClient, setAccessControlClient] = useState<DatabaseClient | null>(null);

  // Local overrides for user_count after access control changes, keyed by client_id
  const [userCountOverrides, setUserCountOverrides] = useState<Map<number, number>>(new Map());

  // Handle client name change and check for duplicates
  const handleClientNameChange = (value: string) => {
    setNewClientName(value);
    
    if (value.trim().length > 2) {
      // Get existing client names for duplicate checking
      const existingClientNames = isUsingDatabaseApproach
        ? dbClientsArray.map(client => client.name)
        : clients;
      
      // Check for similarity matches
      const matches = clientSimilarityService.checkForDuplicates(value, existingClientNames);
      setSimilarityMatches(matches);
      setShowDuplicateWarning(matches.length > 0);
    } else {
      setSimilarityMatches([]);
      setShowDuplicateWarning(false);
    }
  };

  // Function to proceed with client creation despite duplicates
  const proceedWithDuplicateClientCreation = () => {
    setShowDuplicateWarning(false);
    setSimilarityMatches([]);
    handleCreateClient();
  };
  
  // Handle search - passed to SearchInput component
  const handleSearch = useCallback((searchTerm: string) => {
    if (onSearchChange) {
      onSearchChange(searchTerm);
    }
  }, [onSearchChange]);

  const dbClientsArray = Array.isArray(dbClients) ? dbClients : [];

  const handleToggleClient = (clientId: string | number) => {
    const clientIdStr = clientId.toString();
    setExpandedClients(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clientIdStr)) {
        newSet.delete(clientIdStr);
      } else {
        newSet.add(clientIdStr);
        toggleClientExpansion(clientId);
      }
      return newSet;
    });
  };

  const handleSelectWorkflow = (clientId: string | number, workflowId: string | number, workflow: any) => {
    const clientIdStr = clientId.toString();
    const workflowIdStr = workflowId.toString();
    
    if (selectedWorkflowDetails && 
        selectedWorkflowDetails.clientId === clientIdStr && 
        selectedWorkflowDetails.workflowId === workflowIdStr) {
      setSelectedWorkflowDetails(null);
    } else {
      // Store both the string representation for UI consistency and the original object
      // Also ensure we have the numeric database ID when available
      const consistentWorkflow = {
        ...workflow,
        // Ensure we always have a workflow_id property (as a number if possible)
        workflow_id: workflow.workflow_id || 
          (typeof workflowId === 'number' ? workflowId : 
            (/^\d+$/.test(workflowIdStr) ? parseInt(workflowIdStr, 10) : workflowIdStr))
      };
      
      setSelectedWorkflowDetails({
        clientId: clientIdStr,
        workflowId: workflowIdStr,
        workflow: consistentWorkflow
      });
      
      toggleWorkflowExpansion(clientIdStr, workflowIdStr);
    }
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim()) {
      showToast('Please enter a client name', 'error');
      return;
    }

    try {
      setIsCreatingClient(true);
      let newClientId = '';
      let newDbClient: DatabaseClient | null = null;

      if (isUsingDatabaseApproach) {
        const clientCode = newClientName
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');

        const newClient = await workflowService.createDbClient(
          newClientName,
          clientCode,
          true
        );

        localStorage.setItem('db_client_id', newClient.client_id.toString());
        if (setDbClientId) {
          setDbClientId(newClient.client_id);
        }
        
        newClientId = newClient.client_id.toString();
        newDbClient = newClient;

        showToast(`Client "${newClientName}" created successfully`, 'success');

        if (loadClients) {
          await loadClients();
        }
      } else {
        const clientId = newClientName
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');

        if (clients.includes(clientId)) {
          showToast('A client with this name already exists', 'error');
          return;
        }

        localStorage.setItem('selected_client_id', clientId);
        newClientId = clientId;

        showToast(`Client "${clientId}" created successfully`, 'success');

        if (loadClients) {
          await loadClients();
        }
      }

      setCreateDialogOpen(false);
      setNewClientName('');
      
      // After successful client creation, prompt for workflow creation
      if (isUsingDatabaseApproach && newDbClient) {
        setSelectedClient(newDbClient);
      } else {
        setSelectedClient(newClientId);
      }
      
      setPromptWorkflowAfterClientCreation(true);
      setCreateWorkflowDialogOpen(true);
      
      // Expand the newly created client in the list
      setExpandedClients(prev => {
        const newSet = new Set(prev);
        newSet.add(newClientId);
        return newSet;
      });
      toggleClientExpansion(newClientId);
      
    } catch (error) {
      console.error('Error creating client:', error);
      showToast('Failed to create client', 'error');
    } finally {
      setIsCreatingClient(false);
    }
  };

  const handleOpenCreateWorkflow = (client: string | DatabaseClient) => {
    setSelectedClient(client);
    setCreateWorkflowDialogOpen(true);

    if (isUsingDatabaseApproach && typeof client !== 'string') {
      localStorage.setItem('db_client_id', client.client_id.toString());
      if (setDbClientId) {
        setDbClientId(client.client_id);
      }
    } else if (typeof client === 'string') {
      localStorage.setItem('selected_client_id', client);
    }
  };

  const handleCreateWorkflow = async () => {
    const workflowName = workflowNameRef.current?.value?.trim() ?? '';
    if (!workflowName) {
      showToast('Please enter a workflow name', 'error');
      return;
    }

    if (!selectedClient) {
      showToast('No client selected', 'error');
      return;
    }

    try {
      setIsCreatingWorkflow(true);      

      const effectiveClientId = selectedClient && typeof selectedClient === 'string'
        ? selectedClient
        : selectedClient ? (selectedClient as any).client_id?.toString() : null;
            
      // Check if database-first approach is enabled
      // Previously was checking for localStorage value directly, which might not match component state
      if (isUsingDatabaseApproach) {
        try {
          // Debug logging
          console.log('Creating workflow with database-first approach');
          console.log('Client information:', {
            selectedClient,
            effectiveClientId,
            isUsingDatabaseApproach
          });
          
          if (!effectiveClientId) {
            showToast('No client selected. Please go back to client selection.', 'error');
            navigate('/client-selection');
            return;
          }
          
          // Validate client ID format - only allow alphanumeric characters and hyphens
          if (!/^[a-zA-Z0-9-]+$/.test(effectiveClientId)) {
            console.error('Invalid client ID format:', effectiveClientId);
            showToast('Invalid client ID format. Client IDs can only contain letters, numbers, and hyphens.', 'error');
            return;
          }

          let clientId: number;
          
          // If selectedClient is already a DatabaseClient object, use its client_id directly
          if (typeof selectedClient !== 'string' && selectedClient?.client_id) {
            console.log('Using client_id directly from selectedClient:', selectedClient.client_id);
            clientId = selectedClient.client_id;
          } else {
            // Otherwise, get client database ID by client code
            const clients = await workflowService.getDbClients();
            console.log('Retrieved database clients:', clients);
            
            // Find the client that matches the effectiveClientId
            const client = clients.find(c => c.client_code === effectiveClientId);
            console.log('Found client:', client);
            
            if (!client) {
              console.error('Client not found in database:', effectiveClientId);
              throw new Error('Client not found in database, falling back to standard approach');
            }
            
            clientId = client.client_id;
          }
          
          console.log('Creating workflow for client ID:', clientId);
          
          // Verify that client ID is valid numeric ID
          if (typeof clientId !== 'number' || isNaN(clientId) || clientId <= 0) {
            console.error(`Invalid client ID format for database workflow creation: ${clientId}`);
            throw new Error(`Client ID must be a positive number, got: ${clientId}`);
          }
          
          // Log API call details for debugging
          console.log(`API call: POST /api/v2/db/client/${clientId}/workflows with name=${workflowName}`);
          
          // Create workflow with database approach (now using v2 API endpoint)
          const dbWorkflow = await workflowService.createDbWorkflow(
            clientId, 
            workflowName,
            'Created from Module 1 UI with database-first approach'
          );
          
          console.log('Database workflow created:', dbWorkflow);
          
          // Store the workflow ID and set flag for database-first approach
          localStorage.setItem('db_workflow_id', dbWorkflow.workflow_id.toString());
          localStorage.setItem('use_database_first', 'true');
          
          // Store external ID in localStorage if available
          if (dbWorkflow.external_id) {
            localStorage.setItem(`workflow_${dbWorkflow.workflow_id}_external_id`, dbWorkflow.external_id);
          }
          
          // Store the workflow ID - this is now the database ID
          localStorage.setItem('db_workflow_id', dbWorkflow.workflow_id.toString());
          
          // Close dialog and reset state before navigating
          setCreateWorkflowDialogOpen(false);
          setIsCreatingWorkflow(false);
          
          showToast('Workflow created successfully using database-first approach', 'success');
          
          // Navigate to workflow using the hook (automatically detects v1 vs v2)
          await navigateToWorkflow({
            clientId: clientId,
            workflowId: dbWorkflow.workflow_id
          });
          
          return;
                     
        } catch (dbError) {
          console.error('Database-first workflow creation failed:', dbError);
          showToast('Database-first workflow creation failed, trying standard approach', 'warning');
          // Fall through to the standard approach
        }
      }

      // Standard approach (fallback)
      // Generate a workflow ID
      const newWorkflowId = uuidv4();
      
      // Create workflow data WITHOUT client_id in the main body
      const workflowData = {
        workflow_id: newWorkflowId,
        business_name: workflowName,
        industry: 'General',
        business_rules: [
          {
            rule_type: "default",
            pattern: "default",
            category: "default"
          }
        ],
        additional_context: 'Created from Module 1 UI'
      };
      
      console.log('Using standard approach to create workflow');
      console.log('Using client ID:', effectiveClientId);
      console.log('Request body:', JSON.stringify(workflowData, null, 2));
      
      // Use direct fetch approach
      try {
        // Build URL with query parameter for client_id
        const apiUrl = `${config.api.module1}/api/v1/business-config/?client_id=${encodeURIComponent(effectiveClientId)}`;
        
        // Make direct fetch call
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(typeof window !== 'undefined' && localStorage.getItem('authguard_access_token')
              ? { Authorization: `Bearer ${localStorage.getItem('authguard_access_token')}` }
              : {}),
          },
          body: JSON.stringify(workflowData)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`API error: ${response.status} ${JSON.stringify(errorData)}`);
        }
        
        const result = await response.json();
        console.log('Direct fetch successful:', result);
        
        // Check if this is a v2 workflow by checking the name (case insensitive)
        const isV2Workflow = workflowName.toLowerCase().startsWith('__v2__');
        
        // Navigate to the new workflow with client ID in the route
        if (isV2Workflow) {
          console.log(`Navigating to v2 workflow: /workflow2/${effectiveClientId}/${result.workflow_id}`);
          navigate(`/workflow2/${effectiveClientId}/${result.workflow_id}`);
        } else {
          navigate(`/module1/client/${effectiveClientId}/${result.workflow_id}`);
        }
        showToast('Workflow created successfully', 'success');
        setCreateWorkflowDialogOpen(false);
        setIsCreatingWorkflow(false);
        return;
      } catch (fetchError) {
        console.error('Direct fetch failed:', fetchError);
        showToast('Failed to create workflow', 'error');
        setIsCreatingWorkflow(false);
      }
    } catch (error) {
      console.error('Error creating workflow:', error);
      showToast('Failed to create workflow', 'error');
      setIsCreatingWorkflow(false);
    }
  };

  const handleCloseWorkflowDialog = () => {
    setCreateWorkflowDialogOpen(false);
    setPromptWorkflowAfterClientCreation(false);
  };



  const handleOpenAccessControl = (client: DatabaseClient) => {
    setAccessControlClient(client);
    setAccessControlModalOpen(true);
  };

  const handleCloseAccessControl = (updatedUserCount?: number) => {
    if (updatedUserCount !== undefined && accessControlClient) {
      setUserCountOverrides(prev => {
        const next = new Map(prev);
        next.set(accessControlClient.client_id, updatedUserCount);
        return next;
      });
    }
    setAccessControlModalOpen(false);
    setAccessControlClient(null);
  };

  const getWorkflowStatusChip = (status: string) => {
    let color = 'default';

    switch (status) {
      case 'draft':
      case 'created':
        color = 'primary';
        break;
      case 'finalized':
      case 'completed':
        color = 'success';
        break;
      case 'processing':
        color = 'warning';
        break;
      case 'failed':
        color = 'error';
        break;
      default:
        color = 'default';
    }

    return <Chip size="small" label={status} color={color as any} />;
  };

  const getConsistentWorkflowId = (workflow: any): string => {
    // Always prioritize database IDs when available
    if (workflow.workflow_id) {
      return workflow.workflow_id.toString();
    }
    
    // Fallback for legacy external IDs only when database ID is not available
    const fallbackId = workflow.external_id || workflow.id || '';
    
    // Check if the fallback ID is numeric - if so, it might be a numeric ID stored as string
    if (typeof fallbackId === 'string' && /^\d+$/.test(fallbackId)) {
      console.log(`Potential numeric ID stored as string: ${fallbackId}`);
    }
    
    return fallbackId.toString();
  };

  const getClientStatusColor = (status?: 'new' | 'in_progress' | 'completed'): string => {
    switch (status) {
      case 'completed':
        return theme.palette.success.main; // Green
      case 'in_progress':
        return theme.palette.warning.main; // Orange
      case 'new':
      default:
        return theme.palette.text.secondary; // Gray
    }
  };

  // Helper function to format dates for workflow display
  const formatWorkflowDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      // Format as: Jan 15, 2024
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getClientStatusBadge = (status?: 'new' | 'in_progress' | 'completed') => {
    switch (status) {
      case 'completed':
        return (
          <CheckCircle2 
            size={12} 
            style={{ 
              color: '#fff',
              backgroundColor: theme.palette.success.main,
              borderRadius: '50%',
              padding: '1px'
            }} 
          />
        );
      case 'in_progress':
        return (
          <Clock 
            size={12} 
            style={{ 
              color: '#fff',
              backgroundColor: theme.palette.warning.main,
              borderRadius: '50%',
              padding: '1px'
            }} 
          />
        );
      case 'new':
      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        p: 2,
        position: 'sticky',
        top: 0,
        backgroundColor: 'background.default',
        zIndex: 10,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        borderRadius: '8px'
      }}>
        <GradientTypography variant="h5" component="h1" sx={{ mb: 1 }}>
          Clients & Workflows
        </GradientTypography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <SearchInput
            placeholder="Search by name, ID, or code..."
            tooltipText="Search by client name, client ID, or client code"
            onSearch={handleSearch}
            searchValue={propSearchTerm}
          />
          {isUsingDatabaseApproach && (
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel id="process-status-filter-label">Process Status</InputLabel>
              <Select
                labelId="process-status-filter-label"
                id="process-status-filter"
                value={processStatus}
                label="Process Status"
                onChange={(e) => onProcessStatusChange && onProcessStatusChange(e.target.value)}
                sx={{
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.divider, 0.5),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="new">
                  <Chip size="small" label="New" color="default" />
                </MenuItem>
                <MenuItem value="in_progress">
                  <Chip size="small" label="In Progress" color="warning" />
                </MenuItem>
                <MenuItem value="completed">
                  <Chip size="small" label="Completed" color="success" />
                </MenuItem>
              </Select>
            </FormControl>
          )}
          {isUsingDatabaseApproach && (
            <>
              <FormControl sx={{ minWidth: 180 }} size="small">
                <InputLabel id="sort-by-label">Sort By</InputLabel>
                <Select
                  labelId="sort-by-label"
                  id="sort-by-select"
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => onSortByChange && onSortByChange(e.target.value)}
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.divider, 0.5),
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <MenuItem value="name">Client Name</MenuItem>
                  <MenuItem value="client_id">Client Number</MenuItem>
                  <MenuItem value="process_status">Process Status</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}>
                <IconButton
                  onClick={onSortOrderToggle}
                  size="small"
                  sx={{
                    borderRadius: '8px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    backgroundColor: 'white',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  {sortOrder === 'asc' ? <ArrowUp size={18} /> : <ArrowDown size={18} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Sync Clients">
                <IconButton
                  onClick={async () => {
                    setSyncing(true);
                    try {
                      const response = await workflowService.syncClientsFromSnowflake();
                      showToast(
                        `Synced ${response.sync_stats?.inserted || 0} new clients from Snowflake`,
                        'success'
                      );
                      // Reload the client list to show the newly synced clients
                      if (loadClients) {
                        loadClients();
                      }
                    } catch (error) {
                      console.error('Error syncing clients:', error);
                      showToast('Failed to sync clients from Snowflake', 'error');
                    } finally {
                      setSyncing(false);
                    }
                  }}
                  size="small"
                  disabled={syncing}
                  sx={{
                    borderRadius: '8px',
                    border: `1px solid ${syncing ? theme.palette.warning.main : alpha(theme.palette.divider, 0.5)}`,
                    backgroundColor: syncing ? alpha(theme.palette.warning.main, 0.15) : 'white',
                    '&:hover': {
                      borderColor: syncing ? theme.palette.warning.main : theme.palette.success.main,
                      backgroundColor: syncing ? alpha(theme.palette.warning.main, 0.15) : alpha(theme.palette.success.main, 0.04),
                    },
                    '& svg': {
                      color: syncing ? theme.palette.warning.main : 'inherit',
                      animation: syncing ? 'spin 1s linear infinite' : 'none',
                    },
                  }}
                >
                  <RefreshCw size={18} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={selectedWorkflowDetails ? 6 : 12}>
          <StyledPaper>
            {/* Pagination for Database Approach - Top */}
            {isUsingDatabaseApproach && !loadingClients && onPageChange && onPageSizeChange && (
              <PaginationControls
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={totalClients}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                itemLabel="clients"
                position="top"
              />
            )}
            
            {loadingClients ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (isUsingDatabaseApproach && dbClientsArray.length === 0) || 
               (!isUsingDatabaseApproach && clients.length === 0) ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                  {propSearchTerm ? "No clients match your search" : "No clients yet"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  No clients available. Please contact your administrator to add clients.
                </Typography>
              </Box>
            ) : (
              <List component="nav" aria-label="clients and workflows" sx={{ p: 0 }}>
                {isUsingDatabaseApproach ? (
                  dbClientsArray.map((client, index) => (
                    <React.Fragment key={client.client_id}>
                      {index > 0 && <Divider />}
                      <ListItem disablePadding>
                        <StyledListItemButton 
                          onClick={() => handleToggleClient(client.client_id)}
                          sx={{ 
                            py: 1.25,
                            borderRadius: 0,
                            borderLeft: expandedClients.has(client.client_id.toString()) 
                              ? `3px solid ${theme.palette.primary.main}` 
                              : '3px solid transparent',
                          }}
                        >
                          <ListItemIcon>
                            {expandedClients.has(client.client_id.toString()) ? 
                              <ChevronDown size={20} style={{ color: theme.palette.primary.main }} /> : 
                              <ChevronRight size={20} />}
                          </ListItemIcon>
                          <ListItemIcon>
                            {(() => {
                              const userCount = userCountOverrides.has(client.client_id)
                                ? userCountOverrides.get(client.client_id)!
                                : (client.user_count || 0);
                              return (
                                <Tooltip title={
                                  isAdmin
                                    ? (userCount > 0
                                        ? `${userCount} user(s) have access — click to manage`
                                        : "No users granted access — click to manage")
                                    : (userCount > 0
                                        ? `${userCount} user(s) have access`
                                        : "No users granted access")
                                } placement="top">
                                  <Badge
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    badgeContent={getClientStatusBadge(client.process_status)}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (isAdmin) handleOpenAccessControl(client);
                                      }}
                                      disabled={!isAdmin}
                                      sx={{ p: 0, '&.Mui-disabled': { pointerEvents: 'auto' } }}
                                    >
                                      {userCount > 0 ? (
                                        <Users size={20} style={{ color: theme.palette.success.main }} />
                                      ) : (
                                        <User size={20} style={{ 
                                          color: expandedClients.has(client.client_id.toString()) 
                                            ? theme.palette.primary.main 
                                            : getClientStatusColor(client.process_status)
                                        }} />
                                      )}
                                    </IconButton>
                                  </Badge>
                                </Tooltip>
                              );
                            })()}
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography
                                sx={{ 
                                  fontWeight: expandedClients.has(client.client_id.toString()) ? 600 : 400,
                                  color: expandedClients.has(client.client_id.toString()) 
                                    ? theme.palette.primary.main 
                                    : theme.palette.text.primary
                                }}
                              >
                                {client.name}
                              </Typography>
                            } 
                            secondary={`ID: ${client.client_id}${client.client_code ? ` • Code: ${client.client_code}` : ''}`}
                          />
                          <Box 
                            className="client-actions"
                            sx={{ 
                              display: 'flex', 
                              ml: 1, 
                              gap: 2,
                              opacity: 0,
                              transition: 'opacity 0.2s ease',
                              '.MuiListItemButton-root:hover &': {
                                opacity: 1,
                              }
                            }}
                          >
                            {isAdmin && (
                              <Tooltip title="Manage user access">
                                <Button
                                  size="small"
                                  startIcon={<Shield size={14} />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenAccessControl(client);
                                  }}
                                  sx={{
                                    color: theme.palette.text.secondary,
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    fontWeight: 400,
                                    minWidth: 'auto',
                                    padding: '4px 8px',
                                    '&:hover': {
                                      backgroundColor: 'transparent',
                                      color: theme.palette.success.main,
                                    },
                                  }}
                                >
                                  Access
                                </Button>
                              </Tooltip>
                            )}
                            <Button
                              size="small"
                              startIcon={<Plus size={14} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenCreateWorkflow(client);
                              }}
                              sx={{
                                color: theme.palette.text.secondary,
                                textTransform: 'none',
                                fontSize: '0.875rem',
                                fontWeight: 400,
                                minWidth: 'auto',
                                padding: '4px 8px',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  color: theme.palette.primary.main,
                                },
                              }}
                            >
                              New Workflow
                            </Button>
                          </Box>
                        </StyledListItemButton>
                      </ListItem>
                      
                      <Collapse 
                        in={expandedClients.has(client.client_id.toString())} 
                        timeout={0} 
                        unmountOnExit
                      >
                        {workflows[client.client_id.toString()] === undefined ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : workflows[client.client_id.toString()].length > 0 ? (
                          <List component="div" disablePadding sx={{ 
                            bgcolor: alpha(theme.palette.primary.light, 0.04),
                            borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                          }}>
                            {workflows[client.client_id.toString()].map((workflow) => {
                              // Use the consistent workflow ID function
                              const workflowId = getConsistentWorkflowId(workflow);
                              const isSelected = selectedWorkflowDetails?.workflowId === workflowId;
                              
                              return (
                                <ListItem 
                                  key={workflowId} 
                                  disablePadding
                                >
                                  <StyledListItemButton 
                                    sx={{ 
                                      pl: 16, 
                                      py: 0.75,
                                      minHeight: '48px',
                                      borderLeft: isSelected 
                                        ? `3px solid ${theme.palette.primary.main}` 
                                        : '3px solid transparent',
                                    }}
                                    selected={isSelected}
                                    onClick={() => {
                                      const workflowName = workflow.name || workflow.business_name || '';
                                      goToWorkflow(client.client_id, workflow.workflow_id, workflowName);
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <Briefcase size={16} style={{ 
                                        color: isSelected ? theme.palette.primary.main : undefined 
                                      }} />
                                    </ListItemIcon>
                                    <ListItemText 
                                      primary={
                                        <Typography
                                          sx={{ 
                                            fontSize: '0.95rem',
                                            fontWeight: isSelected ? 600 : 400,
                                            color: isSelected ? theme.palette.primary.main : undefined
                                          }}
                                        >
                                          {workflow.name || workflowId}
                                        </Typography>
                                      }
                                      secondary={`ID: ${workflowId}`}
                                    />
                                    <Box sx={{ 
                                      display: 'flex', 
                                      gap: 2, 
                                      alignItems: 'center',
                                      flexShrink: 0,
                                      ml: 2
                                    }}>
                                      {workflow.created_by_user && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <User size={12} style={{ color: theme.palette.text.secondary }} />
                                          <Typography variant="caption" color="text.secondary">
                                            {workflow.created_by_user.name || workflow.created_by_user.email}
                                          </Typography>
                                        </Box>
                                      )}
                                      {workflow.created_at && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Calendar size={12} style={{ color: theme.palette.text.secondary }} />
                                          <Typography variant="caption" color="text.secondary">
                                            {formatWorkflowDate(workflow.created_at)}
                                          </Typography>
                                        </Box>
                                      )}
                                      {workflow.updated_at && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Clock size={12} style={{ color: theme.palette.text.secondary }} />
                                          <Typography variant="caption" color="text.secondary">
                                            {formatWorkflowDate(workflow.updated_at)}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </StyledListItemButton>
                                </ListItem>
                              );
                            })}
                          </List>
                        ) : (
                          <Box sx={{ 
                            py: 1.5, 
                            px: 4, 
                            bgcolor: alpha(theme.palette.primary.light, 0.04),
                            borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                          }}>
                            <Typography variant="body2" color="text.secondary">
                              No workflows found for this client.
                            </Typography>
                          </Box>
                        )}
                      </Collapse>
                    </React.Fragment>
                  ))
                ) : (
                  clients.map((clientId, index) => (
                    <React.Fragment key={clientId}>
                      {index > 0 && <Divider />}
                      <ListItem disablePadding>
                        <StyledListItemButton 
                          onClick={() => handleToggleClient(clientId)}
                          sx={{ 
                            py: 1.25,
                            borderRadius: 0,
                            borderLeft: expandedClients.has(clientId) 
                              ? `3px solid ${theme.palette.primary.main}` 
                              : '3px solid transparent',
                          }}
                        >
                          <ListItemIcon>
                            {expandedClients.has(clientId) ? 
                              <ChevronDown size={20} style={{ color: theme.palette.primary.main }} /> : 
                              <ChevronRight size={20} />}
                          </ListItemIcon>
                          <ListItemIcon>
                            <User size={20} style={{ 
                              color: expandedClients.has(clientId) 
                                ? theme.palette.primary.main 
                                : undefined 
                            }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography
                                sx={{ 
                                  fontWeight: expandedClients.has(clientId) ? 600 : 400,
                                  color: expandedClients.has(clientId) 
                                    ? theme.palette.primary.main 
                                    : theme.palette.text.primary
                                }}
                              >
                                {clientId}
                              </Typography>
                            } 
                          />
                          <Box 
                            className="client-actions"
                            sx={{ 
                              display: 'flex', 
                              ml: 1,
                              opacity: 0,
                              transition: 'opacity 0.2s ease',
                              '.MuiListItemButton-root:hover &': {
                                opacity: 1,
                              }
                            }}
                          >
                            <Button
                              size="small"
                              startIcon={<Plus size={14} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenCreateWorkflow(clientId);
                              }}
                              sx={{
                                color: theme.palette.text.secondary,
                                textTransform: 'none',
                                fontSize: '0.875rem',
                                fontWeight: 400,
                                minWidth: 'auto',
                                padding: '4px 8px',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  color: theme.palette.primary.main,
                                },
                              }}
                            >
                              New Workflow
                            </Button>
                          </Box>
                        </StyledListItemButton>
                      </ListItem>
                      
                      <Collapse 
                        in={expandedClients.has(clientId)} 
                        timeout={0} 
                        unmountOnExit
                      >
                        {workflows[clientId] === undefined ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : workflows[clientId].length > 0 ? (
                          <List component="div" disablePadding sx={{ 
                            bgcolor: alpha(theme.palette.primary.light, 0.04),
                            borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                          }}>
                            {workflows[clientId].map((workflow) => {
                              const workflowId = getConsistentWorkflowId(workflow);
                              const isSelected = selectedWorkflowDetails?.workflowId === workflowId;
                              
                              return (
                                <ListItem 
                                  key={workflowId} 
                                  disablePadding
                                >
                                  <StyledListItemButton 
                                    sx={{ 
                                      pl: 16, 
                                      py: 0.75,
                                      minHeight: '48px',
                                      borderLeft: isSelected 
                                        ? `3px solid ${theme.palette.primary.main}` 
                                        : '3px solid transparent',
                                    }}
                                    selected={isSelected}
                                    onClick={() => {
                                      const workflowName = workflow.name || workflow.business_name || '';
                                      goToWorkflow(clientId, workflowId, workflowName);
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <Briefcase size={16} style={{ 
                                        color: isSelected ? theme.palette.primary.main : undefined 
                                      }} />
                                    </ListItemIcon>
                                    <ListItemText 
                                      primary={
                                        <Typography
                                          sx={{ 
                                            fontSize: '0.95rem',
                                            fontWeight: isSelected ? 600 : 400,
                                            color: isSelected ? theme.palette.primary.main : undefined
                                          }}
                                        >
                                          {workflow.business_name || workflowId}
                                        </Typography>
                                      }
                                      secondary={`ID: ${workflowId}`}
                                    />
                                    <Box sx={{ 
                                      display: 'flex', 
                                      gap: 2, 
                                      alignItems: 'center',
                                      flexShrink: 0,
                                      ml: 2
                                    }}>
                                      {workflow.created_by_user && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <User size={12} style={{ color: theme.palette.text.secondary }} />
                                          <Typography variant="caption" color="text.secondary">
                                            {workflow.created_by_user.name || workflow.created_by_user.email}
                                          </Typography>
                                        </Box>
                                      )}
                                      {workflow.created_at && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Calendar size={12} style={{ color: theme.palette.text.secondary }} />
                                          <Typography variant="caption" color="text.secondary">
                                            {formatWorkflowDate(workflow.created_at)}
                                          </Typography>
                                        </Box>
                                      )}
                                      {workflow.updated_at && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Clock size={12} style={{ color: theme.palette.text.secondary }} />
                                          <Typography variant="caption" color="text.secondary">
                                            {formatWorkflowDate(workflow.updated_at)}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </StyledListItemButton>
                                </ListItem>
                              );
                            })}
                          </List>
                        ) : (
                          <Box sx={{ 
                            py: 1.5, 
                            px: 4, 
                            bgcolor: alpha(theme.palette.primary.light, 0.04),
                            borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                          }}>
                            <Typography variant="body2" color="text.secondary">
                              No workflows found for this client.
                            </Typography>
                          </Box>
                        )}
                      </Collapse>
                    </React.Fragment>
                  ))
                )}
              </List>
            )}
            
            {/* Pagination for Database Approach - Bottom */}
            {isUsingDatabaseApproach && !loadingClients && onPageChange && onPageSizeChange && (
              <PaginationControls
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={totalClients}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                itemLabel="clients"
                position="bottom"
              />
            )}
          </StyledPaper>
        </Grid>

        {selectedWorkflowDetails && (
          <Grid item xs={12} md={6}>
            <StyledPaper sx={{ p: 3, mb: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start', 
                mb: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                pb: 1
              }}>
                <GradientTypography variant="h6">
                  {isUsingDatabaseApproach
                    ? (selectedWorkflowDetails.workflow.name || selectedWorkflowDetails.workflowId)
                    : (selectedWorkflowDetails.workflow.business_name || selectedWorkflowDetails.workflowId)}
                </GradientTypography>
                
                <StyledIconButton 
                  size="small" 
                  onClick={() => setSelectedWorkflowDetails(null)}
                  sx={{ 
                    ml: 1,
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  }}
                >
                  <ChevronRight size={20} />
                </StyledIconButton>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                <Chip 
                  icon={<User size={14} />}
                  label={isUsingDatabaseApproach ? 
                    dbClientsArray.find(c => c.client_id.toString() === selectedWorkflowDetails.clientId)?.name || selectedWorkflowDetails.clientId : 
                    selectedWorkflowDetails.clientId
                  } 
                  size="small" 
                  sx={{ 
                    bgcolor: alpha(theme.palette.primary.light, 0.1),
                    '& .MuiChip-icon': {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
                {selectedWorkflowDetails.workflow.type && (
                  <Chip label={`Type: ${selectedWorkflowDetails.workflow.type}`} size="small" />
                )}
              </Box>

              <Box sx={{ display: 'flex', mt: 2, mb: 3, gap: 2 }}>
                <StyledButton
                  variant="contained"
                  onClick={() => {
                    // Get workflow name for routing decision
                    const workflowName = selectedWorkflowDetails.workflow?.name || 
                                        selectedWorkflowDetails.workflow?.business_name || 
                                        '';
                    
                    // Use database IDs when available, fall back to external IDs only as needed
                    if (isUsingDatabaseApproach) {
                      // For database approach, use numeric IDs directly
                      const clientId = parseInt(selectedWorkflowDetails.clientId, 10);
                      const workflowId = selectedWorkflowDetails.workflow.workflow_id 
                        ? selectedWorkflowDetails.workflow.workflow_id 
                        : selectedWorkflowDetails.workflowId;
                      
                      goToWorkflow(clientId, workflowId, workflowName);
                    } else {
                      // For legacy approach, use client ID string and workflow ID
                      goToWorkflow(
                        selectedWorkflowDetails.clientId, 
                        selectedWorkflowDetails.workflowId,
                        workflowName
                      );
                    }
                  }}
                  startIcon={<LayoutDashboard size={16} />}
                  sx={{ borderRadius: '8px' }}
                >
                  Open Dashboard
                </StyledButton>
              </Box>
                
              <Divider sx={{ my: 2 }}/>
              
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mb: 2, 
                  display: 'flex', 
                  alignItems: 'center',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                <File size={18} style={{ marginRight: '8px', color: theme.palette.primary.main }} />
                Files
              </Typography>
                
              {loadingFiles ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <React.Fragment>
                  {files[`${selectedWorkflowDetails.clientId}:${selectedWorkflowDetails.workflowId}`]?.length > 0 ? (
                    <List 
                      dense
                      sx={{ 
                        bgcolor: alpha(theme.palette.background.paper, 0.4),
                        borderRadius: '8px',
                        border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                      }}
                    >
                      {files[`${selectedWorkflowDetails.clientId}:${selectedWorkflowDetails.workflowId}`].map((file, index) => (
                        <ListItem key={index} disablePadding>
                          <StyledListItemButton 
                            onClick={() => handleFileAction(file)}
                            sx={{ borderRadius: 0 }}
                          >
                            <ListItemIcon>
                              <File size={16} style={{ color: theme.palette.info.main }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={file.name}
                              secondary={file.lastModified ? new Date(file.lastModified).toLocaleString() : ''}
                            />
                          </StyledListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Alert 
                      severity="info" 
                      sx={{ 
                        mb: 2,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        '& .MuiAlert-icon': {
                          color: theme.palette.info.main,
                        }
                      }}
                    >
                      No files available for this workflow
                    </Alert>
                  )}
                </React.Fragment>
              )}
            </StyledPaper>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={createDialogOpen}
        onClose={() => !isCreatingClient && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
            backdropFilter: 'blur(8px)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}>
          {/* Use a span as container for the GradientTypography to avoid nesting h6 in h2 */}
          <span>
            <GradientTypography variant="h6" component="span">
              Create New Client
            </GradientTypography>
          </span>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create a new client to organize your data and analysis.
            </Typography>
            <TextField
              label="Client Name"
              fullWidth
              value={newClientName}
              onChange={(e) => handleClientNameChange(e.target.value)}
              disabled={isCreatingClient}
              autoFocus
              error={showDuplicateWarning}
              InputProps={{
                sx: {
                  borderRadius: '8px',
                  transition: theme.transitions.create(['box-shadow']),
                  '&:focus-within': {
                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                  }
                }
              }}
              helperText={
                showDuplicateWarning 
                  ? `Potential duplicate found! ${similarityMatches.length} similar client(s) detected.`
                  : isUsingDatabaseApproach
                    ? "This will be stored in the database with a client code."
                    : `This will be converted to a client ID: ${newClientName
                      ? newClientName.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
                      : 'example-client'}`
              }
            />

            {/* Duplicate Warning Alert */}
            {showDuplicateWarning && similarityMatches.length > 0 && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mt: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                  '& .MuiAlert-icon': {
                    color: theme.palette.warning.main,
                  }
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Similar clients found:
                </Typography>
                <Box sx={{ ml: 2 }}>
                  {similarityMatches.slice(0, 3).map((match, index) => (
                    <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                      • "{match.existingClient}" ({match.similarityScore}% similarity, {match.matchType} match)
                    </Typography>
                  ))}
                  {similarityMatches.length > 3 && (
                    <Typography variant="body2" color="text.secondary">
                      ...and {similarityMatches.length - 3} more
                    </Typography>
                  )}
                </Box>
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setShowDuplicateWarning(false);
                      setSimilarityMatches([]);
                      setNewClientName('');
                    }}
                    sx={{ borderRadius: '6px' }}
                  >
                    Choose Different Name
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    onClick={proceedWithDuplicateClientCreation}
                    disabled={isCreatingClient}
                    sx={{ borderRadius: '6px' }}
                  >
                    Proceed Anyway
                  </Button>
                </Box>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}>
          <StyledButton
            onClick={() => setCreateDialogOpen(false)}
            disabled={isCreatingClient}
            sx={{ borderRadius: '8px' }}
          >
            Cancel
          </StyledButton>
          <StyledButton
            variant="contained"
            onClick={handleCreateClient}
            disabled={isCreatingClient || !newClientName.trim()}
            sx={{ borderRadius: '8px' }}
          >
            {isCreatingClient ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Creating...
              </Box>
            ) : (
              'Create Client'
            )}
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createWorkflowDialogOpen}
        onClose={() => !isCreatingWorkflow && handleCloseWorkflowDialog()}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
            backdropFilter: 'blur(8px)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}>
          {/* Use a span as container for the GradientTypography to avoid nesting h6 in h2 */}
          <span>
            <GradientTypography variant="h6" component="span">
              {promptWorkflowAfterClientCreation 
                ? "Create Workflow for New Client" 
                : "Create New Workflow"}
            </GradientTypography>
          </span>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            {promptWorkflowAfterClientCreation && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  '& .MuiAlert-icon': {
                    color: theme.palette.success.main,
                  }
                }}
              >
                Client created successfully! Would you like to create a workflow for this client now?
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create a new workflow for{' '}
              <strong>
                {selectedClient
                  ? (typeof selectedClient === 'string'
                    ? selectedClient
                    : selectedClient.name)
                  : '(No client selected)'}
              </strong>
            </Typography>

            <Box sx={{ mb: 3 }}>
              <TextField
                label="Workflow Name"
                fullWidth
                inputRef={workflowNameRef}
                defaultValue=""
                disabled={isCreatingWorkflow}
                autoFocus
                InputProps={{
                  sx: {
                    borderRadius: '8px',
                    transition: theme.transitions.create(['box-shadow']),
                    '&:focus-within': {
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    }
                  }
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}>
          <StyledButton
            onClick={handleCloseWorkflowDialog}
            disabled={isCreatingWorkflow}
            sx={{ borderRadius: '8px' }}
          >
            {promptWorkflowAfterClientCreation ? "Skip for now" : "Cancel"}
          </StyledButton>
          <StyledButton
            variant="contained"
            onClick={handleCreateWorkflow}
            disabled={isCreatingWorkflow}
            sx={{ borderRadius: '8px' }}
          >
            {isCreatingWorkflow ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Creating...
              </Box>
            ) : (
              'Create Workflow Now'
            )}
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Access Control Modal */}
      {accessControlClient && (
        <ClientAccessControlModal
          open={accessControlModalOpen}
          onClose={handleCloseAccessControl}
          clientId={accessControlClient.client_id}
          clientName={accessControlClient.name}
        />
      )}

    </Box>
  );
};

export default ClientWorkflowTable;
